import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Portal from "@/components/Portal";
import ZoomableImage from "@/components/ZoomableImage";

// Full-screen image viewer.
// Props:
//  - images: string[] (URLs)
//  - index: number (currently displayed)
//  - onIndexChange: (nextIdx) => void
//  - onClose: () => void
export default function Lightbox({ images = [], index = 0, onIndexChange, onClose }) {
  const total = images.length;
  const prev = useCallback(() => onIndexChange((index - 1 + total) % total), [index, total, onIndexChange]);
  const next = useCallback(() => onIndexChange((index + 1) % total), [index, total, onIndexChange]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && total > 1) prev();
      else if (e.key === "ArrowRight" && total > 1) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next, total]);

  if (!total) return null;
  const src = images[index];

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[90] bg-[#0B0B0C]/98 flex items-center justify-center fade-in"
        data-testid="lightbox"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500"
        style={{ border: "1px solid var(--hairline)" }}
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X size={20} strokeWidth={1} />
      </button>

      {total > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500"
            style={{ border: "1px solid var(--hairline)", background: "rgba(11,11,12,0.6)" }}
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft size={22} strokeWidth={1} />
          </button>
          <button
            onClick={next}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500"
            style={{ border: "1px solid var(--hairline)", background: "rgba(11,11,12,0.6)" }}
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight size={22} strokeWidth={1} />
          </button>
        </>
      )}

      <div className="absolute top-6 left-6 hidden md:block font-micro text-[#8A8680] pointer-events-none" data-testid="lightbox-hint-desktop">
        Scroll to zoom · Drag to pan
      </div>
      <div className="absolute top-6 left-1/2 -translate-x-1/2 md:hidden font-micro text-[#8A8680] pointer-events-none text-center" data-testid="lightbox-hint-mobile">
        Pinch to zoom · Double-tap to toggle
      </div>

      <div className="relative w-[92vw] h-[86vh] flex items-center justify-center" data-testid="lightbox-image-wrap">
        <ZoomableImage src={src} alt="" resetKey={index} />
      </div>

      {total > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 items-center" data-testid="lightbox-dots">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => onIndexChange(i)}
              className="h-[1px] transition-all duration-500"
              style={{ width: i === index ? 32 : 14, background: i === index ? "#B8895C" : "#8A8680" }}
              data-testid={`lightbox-dot-${i}`}
              aria-label={`Image ${i + 1}`}
            />
          ))}
          <div className="ml-3 font-micro text-[#8A8680]" data-testid="lightbox-counter">{index + 1} / {total}</div>
        </div>
      )}
      </div>
    </Portal>
  );
}
